import {
  AspectRatio,
  VideoDimensionsHeight,
  VideoDimensionsWidth,
} from '../../types.ts/video';
import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { MediaPositioner } from '../models/aspectRatio/MediaPositioner';
import { AbstractLayoutCommand } from './AbstractLayoutCommand';
import { TimelineMode } from './TimelineMode';
import { UpdateElementCommand } from './UpdateElementCommand';

import type { VideoElement } from '../models';
import type { Element } from '../models/Element';
import type { NaturalDimensions, VideoDimensions } from '../types/aspectRatio';
import type { VideoEditor } from '../VideoEditor';

export interface ClearLayoutElementsParameters {
  fit: 'cover' | 'contain' | 'fill';
  videoSourceDimensionsPrev?: VideoDimensions;
  videoSourceDimensionsCurr?: VideoDimensions;
  naturalMediaDimensions?: Record<VideoElement['source'], NaturalDimensions>;
}

export class ClearLayoutElementsCommand extends AbstractLayoutCommand {
  private params: ClearLayoutElementsParameters;

  getName(): string {
    return 'ClearLayoutElementsCommand';
  }

  constructor(
    editor: VideoEditor,
    element: Element,
    params: ClearLayoutElementsParameters,
  ) {
    super(editor, element);
    this.params = params;
  }

  execute(): boolean {
    if (!this.sourceElement) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'No source element provided',
      });
      return false;
    }

    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.sourceElement.id,
      extraElementData: this.extraElementData,
    });

    super.execute();

    const elements = this.pickAllLayoutElements(this.sourceElement);
    if (!elements) {
      return false;
    }
    const { parent: parentElement, children: childrenElements } = elements;

    for (const linkedElement of childrenElements) {
      this.extraElementData.removeElementData(linkedElement.id);
      const success = this.editor
        .getTrackManager()
        .removeElement(linkedElement, TimelineMode.DEFAULT);
      if (!success) {
        return false;
      }
    }

    this.extraElementData.clearLayoutData(parentElement);

    let command = new UpdateElementCommand(this.editor, parentElement.id, {
      visible: true,
      locked: false,
      ...this.getElementAdjustmentsForAspectRatio(parentElement),
      fit: this.params.fit,
    });
    let success = this.editor.executeCommand(command);
    if (!success) {
      return false;
    }

    this.canUndo = true;

    return true;
  }

  getDescription(): string {
    return `exiting layout mode`;
  }

  private getElementAdjustmentsForAspectRatio(
    element: VideoElement,
  ): Record<'width' | 'height' | 'x' | 'y', string> {
    // defaults are for 9:16, the only supported aspect ratio
    const {
      videoSourceDimensionsPrev = {
        width: VideoDimensionsWidth.AR_16_9,
        height: VideoDimensionsHeight.AR_16_9,
        aspectRatio: AspectRatio.AR_16_9,
      },
      videoSourceDimensionsCurr = {
        width: VideoDimensionsWidth.AR_9_16,
        height: VideoDimensionsHeight.AR_9_16,
        aspectRatio: AspectRatio.AR_9_16,
      },
      fit,
    } = this.params;

    if (['fill', 'contain'].includes(fit)) {
      return MediaPositioner.getDefaultPositioning();
    }

    // Create a media positioner with the dimensions needed for calculations
    const mediaPositioner = new MediaPositioner(
      videoSourceDimensionsPrev,
      videoSourceDimensionsCurr,
    );

    // Get adjusted dimensions from the positioner
    const naturalDimensions = this.getNaturalDimensionsForElement(element);
    return mediaPositioner.getAdjustedDimensions(naturalDimensions);
  }

  private getNaturalDimensionsForElement(
    element: VideoElement,
  ): NaturalDimensions | undefined {
    return this.params.naturalMediaDimensions?.[element.source];
  }
}
